import type { useTranslation } from 'react-i18next';

import type { PmTask, RecentActivityItem } from '@/api/generated/models';
import { PmTaskCategory } from '@/api/generated/models/pmTaskCategory';
import { PmTaskPriority } from '@/api/generated/models/pmTaskPriority';
import { currentLocale, formatDate } from '@/utils/format';

/** Isti vokabular kao Board (board.period.*) — samo filter semantika razlikuje: ovdje since
 * postaje dueFrom (donja granica Roka za otvorene stavke / datuma dovršetka za Završeno). */
export const PM_PERIOD_OPTIONS = [
  { value: 'LAST_30_DAYS', labelKey: 'board.period.last30Days' },
  { value: 'LAST_3_MONTHS', labelKey: 'board.period.last3Months' },
  { value: 'LAST_6_MONTHS', labelKey: 'board.period.last6Months' },
  { value: 'THIS_YEAR', labelKey: 'board.period.thisYear' },
  { value: 'ALL', labelKey: 'board.period.all' },
  { value: 'CUSTOM', labelKey: 'board.period.custom' }
];

export const buildPmPeriodOptions = (t: ReturnType<typeof useTranslation>['t']) =>
  PM_PERIOD_OPTIONS.map((option) => ({ value: option.value, label: t(option.labelKey) }));

/** Zrcali BoardPeriod.since(today) na backendu (com.externo.cxm.board.BoardPeriod) — undefined = bez donje granice. */
export const resolvePmPeriodSince = (period: string | null, today: Date): Date | undefined => {
  const since = new Date(today);
  switch (period) {
    case 'LAST_30_DAYS':
      since.setDate(since.getDate() - 30);
      return since;
    case 'LAST_3_MONTHS':
      since.setMonth(since.getMonth() - 3);
      return since;
    case 'LAST_6_MONTHS':
      since.setMonth(since.getMonth() - 6);
      return since;
    case 'THIS_YEAR':
      return new Date(today.getFullYear(), 0, 1);
    default:
      return undefined;
  }
};

export const PM_STATUS_FILTER_OPTIONS = [
  { value: 'ALL', labelKey: 'pmDashboard.filterStatusAll' },
  { value: PmTaskCategory.TODO, labelKey: 'pmDashboard.category.todo' },
  { value: PmTaskCategory.REVIEW, labelKey: 'pmDashboard.category.review' },
  { value: PmTaskCategory.IN_PROGRESS, labelKey: 'pmDashboard.category.inProgress' },
  { value: PmTaskCategory.DONE, labelKey: 'pmDashboard.category.done' }
];

export const buildPmStatusFilterOptions = (t: ReturnType<typeof useTranslation>['t']) =>
  PM_STATUS_FILTER_OPTIONS.map((option) => ({ value: option.value, label: t(option.labelKey) }));

export const pmTaskCategoryConfig: Record<
  PmTaskCategory,
  { labelKey: string; bg: string; fg: string }
> = {
  [PmTaskCategory.TODO]: {
    labelKey: 'pmDashboard.category.todo',
    bg: 'var(--color-status-warning-soft)',
    fg: 'var(--color-status-warning)'
  },
  [PmTaskCategory.REVIEW]: {
    labelKey: 'pmDashboard.category.review',
    bg: 'var(--color-status-info-soft)',
    fg: 'var(--color-status-info)'
  },
  [PmTaskCategory.IN_PROGRESS]: {
    labelKey: 'pmDashboard.category.inProgress',
    bg: 'var(--color-accent-secondary-soft)',
    fg: 'var(--color-accent-secondary)'
  },
  [PmTaskCategory.DONE]: {
    labelKey: 'pmDashboard.category.done',
    bg: 'var(--color-status-success-soft)',
    fg: 'var(--color-status-success)'
  }
};

export const pmTaskPriorityColor = (priority?: PmTaskPriority): string => {
  switch (priority) {
    case PmTaskPriority.HIGH:
      return 'var(--color-status-danger)';
    case PmTaskPriority.MEDIUM:
      return 'var(--color-status-warning)';
    default:
      return 'var(--color-text-muted)';
  }
};

export const pmTaskPriorityLabelKey = (priority?: PmTaskPriority): string => {
  switch (priority) {
    case PmTaskPriority.HIGH:
      return 'pmDashboard.priority.high';
    case PmTaskPriority.MEDIUM:
      return 'pmDashboard.priority.medium';
    default:
      return 'pmDashboard.priority.low';
  }
};

/** Mobilna kartica (Figma NP-01M) spaja ugovor/ponudu + objekt + klijent u jednu rečenicu, za
 * razliku od desktop tablice koja ih prikazuje u dva reda. */
export const formatPmTaskSubtitle = (
  t: ReturnType<typeof useTranslation>['t'],
  task: PmTask
): string => {
  const entity = task.contractNumber
    ? t('pmDashboard.table.contractLabel', { number: task.contractNumber })
    : t('pmDashboard.table.offerLabel', { number: task.offerNumber });
  return [entity, task.objectName, task.clientOrgName].filter(Boolean).join(' · ');
};

/** Primarni akcijski gumb na mobilnoj kartici (Figma NP-01M) — svaki tip ima glagolski naslov i
 * navigira na stvarni entitet gdje se akcija zapravo odvija (nema inline mutacija na dashboardu). */
export const pmTaskActionLabelKey = (task: PmTask): string => {
  switch (task.type) {
    case 'SEND_WEEK_REPORT':
      return task.category === PmTaskCategory.DONE
        ? 'pmDashboard.action.openWeekReport'
        : 'pmDashboard.action.sendWeekReport';
    case 'CORRECT_WEEK_REPORT':
      return 'pmDashboard.action.correctWeekReport';
    case 'INVOICE_SENT_AWAITING_CLIENT':
    case 'INVOICE_PENDING_MY_DECISION':
    case 'INVOICE_REJECTED':
    case 'INVOICE_OVERDUE':
    case 'INVOICE_PAID':
      return task.invoiceId ? 'pmDashboard.action.openInvoice' : 'pmDashboard.action.openContract';
    case 'WEEK_REPORT_WAITING_ON_CLIENT':
    case 'WEEK_REPORT_PENDING_MY_APPROVAL':
      return task.weekReportId
        ? 'pmDashboard.action.openWeekReport'
        : 'pmDashboard.action.openContract';
    case 'OFFER_ACCEPTED_NO_CONTRACT':
    case 'OFFER_DRAFT_NOT_SENT':
    case 'OFFER_PENDING_MY_DECISION':
    case 'OFFER_SENT_AWAITING_CLIENT':
      return task.offerId ? 'pmDashboard.action.openOffer' : 'pmDashboard.action.openContract';
    default:
      return 'pmDashboard.action.openContract';
  }
};

export const pmTaskActionHref = (task: PmTask): string | undefined => {
  if (task.type === 'SEND_WEEK_REPORT' && task.weekReportId) {
    return `/weekly-reports/${task.weekReportId}`;
  }
  if (task.type === 'CORRECT_WEEK_REPORT' && task.weekReportId) {
    return `/weekly-reports/${task.weekReportId}/correction`;
  }
  if (
    (task.type === 'WEEK_REPORT_WAITING_ON_CLIENT' ||
      task.type === 'WEEK_REPORT_PENDING_MY_APPROVAL') &&
    task.weekReportId
  ) {
    return `/weekly-reports/${task.weekReportId}`;
  }
  if (task.invoiceId) return `/invoices/${task.invoiceId}`;
  if (task.contractId) return `/contracts/${task.contractId}`;
  if (task.offerId) return `/offers/${task.offerId}`;
  return undefined;
};

const weekLabel = (t: ReturnType<typeof useTranslation>['t'], task: PmTask) =>
  t('pmDashboard.weekLabel', { week: task.weekNumber, year: task.reportYear });

/**
 * Backend šalje strukturirane podatke (tip + polja), ne gotov tekst — isti princip kao
 * formatBoardAlert (boardDisplay.ts). CONTRACT_ACCEPTED_NOT_ACTIVATED i OFFER_ACCEPTED_NO_CONTRACT
 * u DONE kategoriji stižu bez offerNumber (vidi PmDashboardService.doneContractTask) pa granaju na
 * contractNumber — isto za SEND_WEEK_REPORT kad je odobren (completedAt popunjen).
 */
export const formatPmTaskTitle = (
  t: ReturnType<typeof useTranslation>['t'],
  task: PmTask
): string => {
  const isDone = task.category === PmTaskCategory.DONE;
  switch (task.type) {
    case 'SEND_WEEK_REPORT':
      return isDone
        ? t('pmDashboard.title.weekReportApproved', {
            week: weekLabel(t, task),
            contractNumber: task.contractNumber
          })
        : t('pmDashboard.title.sendWeekReport', {
            week: weekLabel(t, task),
            contractNumber: task.contractNumber
          });
    case 'CORRECT_WEEK_REPORT':
      return t('pmDashboard.title.correctWeekReport', {
        week: weekLabel(t, task),
        contractNumber: task.contractNumber
      });
    case 'WEEK_REPORT_WAITING_ON_CLIENT':
      return t('pmDashboard.title.weekReportWaiting', {
        week: weekLabel(t, task),
        contractNumber: task.contractNumber
      });
    case 'WEEK_REPORT_PENDING_MY_APPROVAL':
      return t('pmDashboard.title.weekReportPendingMyApproval', {
        week: weekLabel(t, task),
        contractNumber: task.contractNumber
      });
    case 'ACTIVITIES_NOT_SUBMITTED':
      return t('pmDashboard.title.activitiesNotSubmitted', {
        week: weekLabel(t, task),
        contractNumber: task.contractNumber
      });
    case 'NO_REALIZATION_THIS_WEEK':
      return t('pmDashboard.title.noRealizationThisWeek', {
        week: weekLabel(t, task),
        contractNumber: task.contractNumber
      });
    case 'CONTRACT_NOT_STARTED':
      return t('pmDashboard.title.contractNotStarted', {
        contractNumber: task.contractNumber,
        date: formatDate(task.referenceDate)
      });
    case 'CONTRACT_BELOW_PLAN':
      return t('pmDashboard.title.contractBelowPlan', { contractNumber: task.contractNumber });
    case 'OFFER_ACCEPTED_NO_CONTRACT':
      return isDone || !task.offerNumber
        ? t('pmDashboard.title.contractCreatedFromOffer', { contractNumber: task.contractNumber })
        : t('pmDashboard.title.offerAcceptedNoContract', { offerNumber: task.offerNumber });
    case 'CONTRACT_ACCEPTED_NOT_ACTIVATED':
      return isDone
        ? t('pmDashboard.title.contractActivated', { contractNumber: task.contractNumber })
        : t('pmDashboard.title.contractAcceptedNotActivated', {
            contractNumber: task.contractNumber
          });
    case 'OFFER_DRAFT_NOT_SENT':
      return t('pmDashboard.title.offerDraftNotSent', { offerNumber: task.offerNumber });
    case 'OFFER_PENDING_MY_DECISION':
      return t('pmDashboard.title.offerPendingMyDecision', { offerNumber: task.offerNumber });
    case 'OFFER_SENT_AWAITING_CLIENT':
      return t('pmDashboard.title.offerSentAwaitingClient', { offerNumber: task.offerNumber });
    case 'CONTRACT_PENDING_MY_DECISION':
      return t('pmDashboard.title.contractPendingMyDecision', {
        contractNumber: task.contractNumber
      });
    case 'CONTRACT_SENT_AWAITING_OTHER':
      return t('pmDashboard.title.contractSentAwaitingOther', {
        contractNumber: task.contractNumber
      });
    case 'INVOICE_SENT_AWAITING_CLIENT':
      return t('pmDashboard.title.invoiceSentAwaitingClient', {
        invoiceNumber: task.invoiceNumber
      });
    case 'INVOICE_PENDING_MY_DECISION':
      return t('pmDashboard.title.invoicePendingMyDecision', { invoiceNumber: task.invoiceNumber });
    case 'INVOICE_REJECTED':
      return t('pmDashboard.title.invoiceRejected', { invoiceNumber: task.invoiceNumber });
    case 'INVOICE_OVERDUE':
      return t('pmDashboard.title.invoiceOverdue', { invoiceNumber: task.invoiceNumber });
    case 'INVOICE_PAID':
      return t('pmDashboard.title.invoicePaid', { invoiceNumber: task.invoiceNumber });
    default:
      return '';
  }
};

const activityLabelKey = (type?: string): string => {
  switch (type) {
    case 'OFFER_ACCEPTED':
      return 'pmDashboard.activity.offerAccepted';
    case 'CONTRACT_CREATED_FROM_OFFER':
      return 'pmDashboard.activity.contractCreatedFromOffer';
    case 'CONTRACT_ACTIVATED':
      return 'pmDashboard.activity.contractActivated';
    case 'WEEK_REPORT_SUBMITTED':
      return 'pmDashboard.activity.weekReportSubmitted';
    case 'WEEK_REPORT_REJECTED':
      return 'pmDashboard.activity.weekReportRejected';
    case 'WEEK_REPORT_APPROVED':
      return 'pmDashboard.activity.weekReportApproved';
    case 'INVOICE_PAID':
      return 'pmDashboard.activity.invoicePaid';
    default:
      return '';
  }
};

export const formatRecentActivity = (
  t: ReturnType<typeof useTranslation>['t'],
  item: RecentActivityItem
): string => t(activityLabelKey(item.type), { label: item.label });

/** item.id je entitetov vlastiti PK (offer/contract/week_report/invoice), ne sintetički ključ. */
export const recentActivityHref = (item: RecentActivityItem): string | undefined => {
  if (!item.id) return undefined;
  switch (item.type) {
    case 'OFFER_ACCEPTED':
      return `/offers/${item.id}`;
    case 'CONTRACT_CREATED_FROM_OFFER':
    case 'CONTRACT_ACTIVATED':
      return `/contracts/${item.id}`;
    case 'WEEK_REPORT_SUBMITTED':
    case 'WEEK_REPORT_REJECTED':
    case 'WEEK_REPORT_APPROVED':
      return `/weekly-reports/${item.id}`;
    case 'INVOICE_PAID':
      return `/invoices/${item.id}`;
    default:
      return undefined;
  }
};

/** Isti princip kao OrgDashboardPage.formatRelative — "danas/jučer, HH:mm" pa puni datum nakon toga. */
export const formatRelativeTimestamp = (
  t: ReturnType<typeof useTranslation>['t'],
  iso?: string
): string => {
  if (!iso) return '—';
  const date = new Date(iso);
  const now = new Date();
  const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const diffDays = Math.round((startOfDay(now) - startOfDay(date)) / (24 * 60 * 60 * 1000));
  const time = date.toLocaleTimeString(currentLocale(), { hour: '2-digit', minute: '2-digit' });
  if (diffDays === 0) return t('dashboard.today', { time });
  if (diffDays === 1) return t('dashboard.yesterday', { time });
  return formatDate(iso);
};
